import { formatDate, formatShortDateTime, type Project } from '@pmai/contracts';
import { type Column, ProgressBar } from '@pmai/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ExternalLink } from '../../shared/external-link';
import { PriorityBadge, ProjectStatusBadge } from './project-badges';

export type ProjectColumn =
  | 'name'
  | 'customer'
  | 'status'
  | 'startDate'
  | 'plannedFinish'
  | 'priority'
  | 'completion'
  | 'updatedAt'
  | 'charter';

/**
 * Project columns, defined once for every project table (dashboard FR-DASH-001/010, customer card
 * FR-CUST-010). A table picks the columns it shows, in its own order.
 */
export function useProjectColumns(): Record<ProjectColumn, Column<Project>> {
  const { t } = useTranslation();
  return useMemo(() => {
    const f = (field: string) => t(`projects.fields.${field}`);
    return {
      name: {
        key: 'name',
        header: f('name'),
        sortKey: 'name',
        cell: (p) => (
          <Link to={`/projects/${p.id}`} className="font-medium text-brand-700 hover:underline">
            {p.name}
          </Link>
        ),
      },
      customer: { key: 'customer', header: f('customer'), sortKey: 'customer', cell: (p) => p.customerName },
      status: {
        key: 'status',
        header: f('status'),
        sortKey: 'status',
        cell: (p) => <ProjectStatusBadge status={p.status} />,
      },
      startDate: {
        key: 'startDate',
        header: f('startDate'),
        sortKey: 'startDate',
        cell: (p) => formatDate(p.startDate),
      },
      plannedFinish: {
        key: 'plannedFinish',
        header: f('plannedFinish'),
        sortKey: 'plannedFinish',
        cell: (p) => formatDate(p.plannedFinish),
      },
      priority: {
        key: 'priority',
        header: f('priority'),
        sortKey: 'priority',
        cell: (p) => <PriorityBadge priority={p.priority} />,
      },
      completion: {
        key: 'completion',
        header: f('completionShort'),
        sortKey: 'completion',
        cell: (p) => <ProgressBar value={p.completion} label={f('completionShort')} />,
      },
      updatedAt: {
        key: 'updatedAt',
        header: f('updatedAt'),
        sortKey: 'updatedAt',
        cell: (p) => formatShortDateTime(p.updatedAt),
      },
      charter: {
        key: 'charter',
        header: f('charter'),
        cell: (p) => <ExternalLink href={p.charterUrl} label={t('projects.charterLink')} />,
      },
    };
  }, [t]);
}
